import React, { createContext, useState, useEffect, useContext } from "react";
import { Auth, API, graphqlOperation, Hub } from "aws-amplify";
import { listUsers, getPricelist, getUser } from "./graphql/queries";
import { createTheme } from "@mui/material/styles";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRecord, setUserRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userGroups, setUserGroups] = useState([]);
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('currentTheme');
    if (savedTheme) {
      return createTheme(JSON.parse(savedTheme));
    }
    // Default theme: black background, light grey links, lime green text
    return createTheme({
      palette: {
        mode: "dark",
        primary: {
          main: "#D3D3D3", // Light grey for links
        },
        secondary: {
          main: "#32FF32", // Lime green for secondary elements
        },
        background: {
          default: "#000000", // Black background
          paper: "#000000",
        },
        text: {
          primary: "#32FF32", // Lime green text
          secondary: "#D3D3D3", // Light grey for secondary text
        },
      },
    });
  });
  const [providerTheme, setProviderTheme] = useState(() => {
    const savedProviderTheme = localStorage.getItem('providerTheme');
    return savedProviderTheme ? createTheme(JSON.parse(savedProviderTheme)) : null;
  });
  const [companyLogo, setCompanyLogo] = useState(null);
  const [activeProvider, setActiveProvider] = useState(null);
  const [userClass, setUserClass] = useState("Customer");
  const [providerInfo, setProviderInfo] = useState(null);

  const getUserGroups = async () => {
    console.log("getUserGroups")
    try {
      const user = await Auth.currentAuthenticatedUser();
      const groups = user.signInUserSession.idToken.payload["cognito:groups"];
      console.log("groups", groups)

      setUserGroups(groups ? groups.map(group => group.toLowerCase()) : []);
      
      if (!groups) {
        setUserClass("Customer");
      } else if (groups.includes("SuperAdmin")) {
        setUserClass("SuperAdmin");
      } else if (groups.includes("Admin")) {
        setUserClass("Admin");
      } else {
        setUserClass("Customer");
      }
    } catch (err) {
      console.error("Error getting user groups:", err);
      setUserGroups([]);
      setUserClass("Customer");
    }
  };

  const fetchUserInfoAndRefresh = async () => {
    console.log("fetchUserInfoAndRefresh")
    
    // First check - if we're on iOS and prevention flag is set, don't even try
    if (navigator.userAgent.includes('iPad') || navigator.userAgent.includes('iPhone')) {
      if (localStorage.getItem('authRetryPrevented') === 'true') {
        console.log("Skipping authentication - iOS device with retry prevention active");
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    try {
      console.log("Attempting to get authenticated user on: ", navigator.userAgent);
      const user = await Auth.currentAuthenticatedUser();
      console.log("got user", user)
      setUser(user);

      
      // Fetch user groups
      const groups = user.signInUserSession.idToken.payload["cognito:groups"];
      console.log("fetchUserInfoAndRefresh groups", groups)
      setUserGroups(groups ? groups.map(group => group.toLowerCase()) : []);

      if (!groups) {
        setUserClass("Customer");
      } else if (groups.includes("SuperAdmin")) {
        setUserClass("SuperAdmin");
      } else if (groups.includes("Admin")) {
        setUserClass("Admin");
      } else {
        setUserClass("Customer");
      }

      await refreshUserAndPricelist(user);
    } catch (error) {
      console.error("Error getting auth data", error);
      console.log("Auth error details:", JSON.stringify(error));
      console.log("Device info:", navigator.userAgent);
      
      // Force clear any potentially corrupted tokens on mobile Safari
      if (navigator.userAgent.includes('iPad') || navigator.userAgent.includes('iPhone')) {
        console.log("Detected iOS device, clearing auth tokens");
        
        // Only attempt to sign out if we haven't already prevented retries
        if (localStorage.getItem('authRetryPrevented') !== 'true') {
          try {
            await Auth.signOut({ global: true });
            // Set flag in localStorage to prevent continuous retry on iOS
            localStorage.setItem('authRetryPrevented', 'true');
            
            // Add timestamp to enable time-based retry control
            localStorage.setItem('authRetryTimestamp', Date.now().toString());
          } catch (signOutError) {
            console.error("Error during forced sign out:", signOutError);
          }
        } else {
          console.log("Auth retry already prevented, skipping sign out");
        }
      }
      
      // Set user to null if not authenticated
      setUser(null);
      setUserRecord(null);
      setUserGroups([]);
      setUserClass("Customer");
    } finally {
      setLoading(false);
    }
  };

  const refreshUserAndPricelist = async (user) => {
    console.log("refreshUserAndPricelist called", user);

    if (!user || !user.attributes) {
      console.error("Invalid user object");
      return;
    }

    const { attributes } = user;

    try {
      // First, find the user by email to get their ID
      const existingUsersResponse = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { email: { eq: attributes.email } },
        })
      );

      console.log("existingUsersResponse:", existingUsersResponse);

      const existingUserFromList = existingUsersResponse?.data?.listUsers?.items?.find(
        (userItem) => userItem.email === attributes.email
      );

      if (!existingUserFromList) {
        console.log("No existing user found, might need to create one");
        return;
      }

      console.log("Found existing user:", existingUserFromList);
      console.log("User price offset type:", existingUserFromList.priceOffsetType);
      console.log("User price offset value:", existingUserFromList.priceOffsetValue);
      
      // Use getUser query to get full user data including activePricelist relation
      let existingUser;
      try {
        const getUserResponse = await API.graphql(
          graphqlOperation(getUser, { id: existingUserFromList.id })
        );
        existingUser = getUserResponse.data.getUser;
        console.log("Fetched full user data with getUser:", existingUser);
        console.log("User activePricelistID:", existingUser.activePricelistID);
        console.log("User activePricelist:", existingUser.activePricelist);
      } catch (getUserError) {
        console.warn("Failed to fetch user with getUser, using listUsers result:", getUserError);
        existingUser = existingUserFromList;
      }
      
      // Set company logo state
      if (existingUser.companyLogo) {
        setCompanyLogo(existingUser.companyLogo);
      }

      // Check if user is Admin/SuperAdmin before fetching pricelist/provider info
      const isAdminOrSuperAdmin = 
        userClass === "Admin" || 
        userClass === "SuperAdmin" ||
        userGroups.includes("admin") ||
        userGroups.includes("superadmin");

      // If getUser didn't include activePricelist (shouldn't happen, but just in case)
      if (existingUser.activePricelistID && !existingUser.activePricelist) {
        console.log("User has activePricelistID but no activePricelist object, fetching it");
        await fetchActivePricelistAndProvider(existingUser);
      } else if (existingUser.activePricelistID && existingUser.activePricelist) {
        console.log("User has activePricelist, fetching provider info");
        // Still need to fetch provider info even if we have the pricelist
        await fetchActivePricelistAndProvider(existingUser);
      } else {
        console.log("User has no active pricelist ID");
      }

      // For Admin/SuperAdmin: Ensure provider theme is cleared and use their own theme
      if (isAdminOrSuperAdmin) {
        console.log("Admin/SuperAdmin detected - ensuring provider theme is cleared");
        setProviderTheme(null);
        setProviderInfo(null);
        localStorage.removeItem('providerTheme');
      }

      setUserTheme(existingUser);
      // Set userRecord AFTER fetching pricelist/provider so it includes activePricelist
      // Create a new object reference to ensure React detects the change
      setUserRecord({ ...existingUser });
        
      // Clear localStorage cache to force reload of pricelist data
      localStorage.removeItem("csvData");
      localStorage.removeItem("uploadedAt");
      localStorage.removeItem("pricelistId");
      localStorage.removeItem("currentPricelistId");
      localStorage.removeItem("currentPricelistCreatedAt");
    } catch (error) {
      console.error("Error refreshing user", error);
    }
  };

  const fetchActivePricelistAndProvider = async (existingUser) => {
    console.log("fetchActivePricelistAndProvider called for user:", existingUser.email);
    console.log("Active pricelist ID:", existingUser.activePricelistID);
    console.log("User class:", userClass);
    console.log("User groups:", userGroups);
    console.log("User createdByUserId:", existingUser.createdByUserId);
    
    // For Admin/SuperAdmin users, always use their own theme, not provider themes
    // Check both userClass state and userGroups as fallback
    const isAdminOrSuperAdmin = 
      userClass === "Admin" || 
      userClass === "SuperAdmin" ||
      userGroups.includes("admin") ||
      userGroups.includes("superadmin");
    
    try {
      // First get the pricelist with provider reference
      const activePricelistResponse = await API.graphql(
        graphqlOperation(getPricelist, { id: existingUser.activePricelistID })
      );
      
      if (!activePricelistResponse.data.getPricelist) {
        console.error("No pricelist found with ID:", existingUser.activePricelistID);
        return;
      }
      
      console.log("Fetched active pricelist:", activePricelistResponse.data.getPricelist);
      existingUser.activePricelist = activePricelistResponse.data.getPricelist;

      // Determine provider: Priority 1) createdByUserId, 2) pricelist owner (userId)
      let providerId = null;
      if (existingUser.createdByUserId && existingUser.createdByUserId !== 'deleted') {
        providerId = existingUser.createdByUserId;
        console.log("Using createdByUserId as provider:", providerId);
      } else if (existingUser.activePricelist && existingUser.activePricelist.userId) {
        providerId = existingUser.activePricelist.userId;
        console.log("Using pricelist owner as provider:", providerId);
      }

      // Fetch the provider user information including logo and theme
      if (providerId) {
        console.log("Fetching provider details for user ID:", providerId);
        
        const providerResponse = await API.graphql(
          graphqlOperation(getUser, { id: providerId })
        );
        
        if (providerResponse.data.getUser) {
          const provider = providerResponse.data.getUser;
          console.log("Fetched provider details:", provider);
          
          setActiveProvider(provider);
          
          // For Admin/SuperAdmin: Don't set providerInfo (which triggers theme changes)
          // and always use their own theme, skip provider theme
          if (isAdminOrSuperAdmin) {
            console.log("User is Admin/SuperAdmin - skipping providerInfo and using their own theme");
            // Clear any existing provider theme and providerInfo
            setProviderTheme(null);
            setProviderInfo(null);
            localStorage.removeItem('providerTheme');
            // Apply user's own theme
            setUserTheme(existingUser);
            return;
          }
          
          // For regular users: Set providerInfo (this will trigger useEffect to apply provider theme)
          setProviderInfo(provider);

          // For regular users: Apply provider's theme if they have one, otherwise fall back to user's theme
          let themeOptions = {};
          if (provider.themeOptions) {
            themeOptions = JSON.parse(provider.themeOptions);
            console.log("Using provider's theme options:", themeOptions);
          } else if (existingUser.themeOptions) {
            themeOptions = JSON.parse(existingUser.themeOptions);
            console.log("Falling back to user's theme options:", themeOptions);
          }

          const newProviderTheme = createTheme({
            palette: {
              primary: {
                main: themeOptions.primaryColor || themeOptions.primary || '#1976d2',
              },
              secondary: {
                main: themeOptions.secondaryColor || themeOptions.secondary || '#dc004e',
              },
              background: {
                default: themeOptions.background || themeOptions.backgroundColor || '#000000',
                paper: themeOptions.backgroundPaper || themeOptions.backgroundPaperColor || '#111111',
              },
              text: {
                primary: themeOptions.textPrimary || themeOptions.textColor || '#ffffff',
                secondary: themeOptions.textSecondary || themeOptions.textSecondaryColor || '#aaaaaa',
              },
            },
          });

          setProviderTheme(newProviderTheme);
          localStorage.setItem('providerTheme', JSON.stringify(newProviderTheme));
          console.log("Applied provider theme:", newProviderTheme);
        }
      } else {
        // Fallback: Set the active provider from the limited pricelist data
        if (existingUser.activePricelist && existingUser.activePricelist.user) {
          console.log("Setting active provider from pricelist data:", existingUser.activePricelist.user);
          setActiveProvider(existingUser.activePricelist.user);
          
          // For Admin/SuperAdmin: Don't set providerInfo (which triggers theme changes)
          if (isAdminOrSuperAdmin) {
            console.log("User is Admin/SuperAdmin - skipping providerInfo and using their own theme");
            setProviderInfo(null);
            setProviderTheme(null);
            localStorage.removeItem('providerTheme');
            setUserTheme(existingUser);
            return;
          }
          
          // For regular users: Set providerInfo
          setProviderInfo(existingUser.activePricelist.user);
        }

        // For Admin/SuperAdmin: Always use their own theme
        if (isAdminOrSuperAdmin) {
          console.log("User is Admin/SuperAdmin - using their own theme");
          setProviderTheme(null);
          localStorage.removeItem('providerTheme');
          setUserTheme(existingUser);
          return;
        }

        // Apply user's own theme as fallback if no provider theme
        // Only set providerTheme if user has theme options, otherwise leave it null
        // so App.js can use currentTheme instead
        if (existingUser.themeOptions) {
          let themeOptions = {};
          try {
            themeOptions = JSON.parse(existingUser.themeOptions);
          } catch (e) {
            console.error("Error parsing user theme options:", e);
            themeOptions = {};
          }

          const newProviderTheme = createTheme({
            palette: {
              primary: {
                main: themeOptions.primaryColor || themeOptions.primary || '#D3D3D3', // Light grey for links (default)
              },
              secondary: {
                main: themeOptions.secondaryColor || themeOptions.secondary || '#32FF32', // Lime green (default)
              },
              background: {
                default: themeOptions.background || themeOptions.backgroundColor || '#000000',
                paper: themeOptions.backgroundPaper || themeOptions.backgroundPaperColor || '#000000',
              },
              text: {
                primary: themeOptions.textPrimary || themeOptions.textColor || '#32FF32', // Lime green text (default)
                secondary: themeOptions.textSecondary || themeOptions.textSecondaryColor || '#D3D3D3', // Light grey (default)
              },
            },
          });

          setProviderTheme(newProviderTheme);
          localStorage.setItem('providerTheme', JSON.stringify(newProviderTheme));
        } else {
          // Clear provider theme if user has no theme options
          setProviderTheme(null);
          localStorage.removeItem('providerTheme');
        }
      }
    } catch (error) {
      console.error("Error fetching active pricelist:", error);
    }
  };

  const setUserTheme = (existingUser) => {
    console.log("setUserTheme called with user:", existingUser.id, existingUser.email);
    if (existingUser.themeOptions) {
      console.log("User has theme options:", existingUser.themeOptions);
      try {
        const themeOptions = JSON.parse(existingUser.themeOptions);
        const newCurrentTheme = createTheme({
          palette: {
            primary: {
              main: themeOptions.primaryColor || themeOptions.primary || '#D3D3D3', // Light grey for links (default)
            },
            secondary: {
              main: themeOptions.secondaryColor || themeOptions.secondary || '#32FF32', // Lime green (default)
            },
            background: {
              default: themeOptions.background || themeOptions.backgroundColor || '#000000',
              paper: themeOptions.backgroundPaper || themeOptions.backgroundPaperColor || '#000000',
            },
            text: {
              primary: themeOptions.textPrimary || themeOptions.textColor || '#32FF32', // Lime green text (default)
              secondary: themeOptions.textSecondary || themeOptions.textSecondaryColor || '#D3D3D3', // Light grey (default)
            },
          },
        });
        console.log("Setting new current theme:", newCurrentTheme.palette);
        setCurrentTheme(newCurrentTheme);
        localStorage.setItem('currentTheme', JSON.stringify(newCurrentTheme));
      } catch (e) {
        console.error("Error parsing theme options:", e);
        // Reset to default theme on parse error
        const defaultTheme = createTheme({
          palette: {
            mode: "dark",
            primary: {
              main: "#D3D3D3", // Light grey for links
            },
            secondary: {
              main: "#32FF32", // Lime green for secondary elements
            },
            background: {
              default: "#000000", // Black background
              paper: "#000000",
            },
            text: {
              primary: "#32FF32", // Lime green text
              secondary: "#D3D3D3", // Light grey for secondary text
            },
          },
        });
        setCurrentTheme(defaultTheme);
        localStorage.setItem('currentTheme', JSON.stringify(defaultTheme));
      }
    } else {
      console.log("User has no theme options, using default theme");
      // Ensure default theme is set with black background, light grey links, lime green text
      const defaultTheme = createTheme({
        palette: {
          mode: "dark",
          primary: {
            main: "#D3D3D3", // Light grey for links
          },
          secondary: {
            main: "#32FF32", // Lime green for secondary elements
          },
          background: {
            default: "#000000", // Black background
            paper: "#000000",
          },
          text: {
            primary: "#32FF32", // Lime green text
            secondary: "#D3D3D3", // Light grey for secondary text
          },
        },
      });
      setCurrentTheme(defaultTheme);
      localStorage.setItem('currentTheme', JSON.stringify(defaultTheme));
    }
  };

  // Effect to refresh theme when providerInfo changes
  // IMPORTANT: Skip this for Admin/SuperAdmin - they should always use their own theme
  useEffect(() => {
    // Don't apply provider themes for Admin/SuperAdmin users
    if (userClass === "Admin" || userClass === "SuperAdmin" || 
        userGroups.includes("admin") || userGroups.includes("superadmin")) {
      console.log("Skipping provider theme application - user is Admin/SuperAdmin");
      return;
    }
    
    if (providerInfo && providerInfo.themeOptions) {
      try {
        const themeOptions = JSON.parse(providerInfo.themeOptions);
        const newProviderTheme = createTheme({
          palette: {
            primary: {
              main: themeOptions.primaryColor || themeOptions.primary || '#D3D3D3', // Light grey for links (default)
            },
            secondary: {
              main: themeOptions.secondaryColor || themeOptions.secondary || '#32FF32', // Lime green (default)
            },
            background: {
              default: themeOptions.background || themeOptions.backgroundColor || '#000000',
              paper: themeOptions.backgroundPaper || themeOptions.backgroundPaperColor || '#000000',
            },
            text: {
              primary: themeOptions.textPrimary || themeOptions.textColor || '#32FF32', // Lime green text (default)
              secondary: themeOptions.textSecondary || themeOptions.textSecondaryColor || '#D3D3D3', // Light grey (default)
            },
          },
        });
        setProviderTheme(newProviderTheme);
        localStorage.setItem('providerTheme', JSON.stringify(newProviderTheme));
        console.log("Provider theme updated from providerInfo change");
      } catch (e) {
        console.error("Error parsing provider theme options:", e);
      }
    } else if (providerInfo && !providerInfo.themeOptions) {
      // Clear provider theme if provider has no theme options
      setProviderTheme(null);
      localStorage.removeItem('providerTheme');
    }
  }, [providerInfo?.themeOptions, userClass, userGroups]);

  useEffect(() => {
    // Check if we're on iOS and if we've already tried and failed to authenticate
    const isIOS = navigator.userAgent.includes('iPad') || navigator.userAgent.includes('iPhone');
    const authRetryPrevented = localStorage.getItem('authRetryPrevented') === 'true';
    
    if (isIOS) {
      // On iOS, we need more strict controls to prevent loops
      if (authRetryPrevented) {
        console.log("Skipping authentication retry on iOS due to previous failure");
        setLoading(false);
        return; // Exit early, don't set up listeners that might retry
      } else {
        // Give a small delay before trying auth on iOS to ensure components are ready
        setTimeout(() => {
          fetchUserInfoAndRefresh();
        }, 500);
      }
    } else {
      // Non-iOS devices can proceed normally
      fetchUserInfoAndRefresh();
    }
    
    // Setup auth listener
    const authListener = Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          // On successful sign in, clear the prevention flag
          localStorage.removeItem('authRetryPrevented');
          localStorage.removeItem('authRetryTimestamp');
          fetchUserInfoAndRefresh();
          break;
        case 'signOut':
          // On sign out, clear the prevention flag but don't retry auth on iOS if prevented
          localStorage.removeItem('authRetryTimestamp');
          
          const currentIsIOS = navigator.userAgent.includes('iPad') || navigator.userAgent.includes('iPhone');
          const currentAuthRetryPrevented = localStorage.getItem('authRetryPrevented') === 'true';
          
          if (!(currentIsIOS && currentAuthRetryPrevented)) {
            // Only attempt to refresh authentication if not on iOS with prevention flag
            fetchUserInfoAndRefresh();
          } else {
            console.log("Skipping auth refresh after signOut on iOS with prevention flag");
            setLoading(false);
          }
          break;
        case 'tokenRefresh':
          // Only proceed with token refresh if not prevented
          if (!(isIOS && authRetryPrevented)) {
            fetchUserInfoAndRefresh();
          }
          break;
        default:
          break;
      }
    });

    return () => {
      Hub.remove('auth', authListener);
    };
  }, []);

  return (
    <UserContext.Provider value={{ 
      user, 
      setUser,
      userRecord, 
      setUserRecord,
      loading, 
      setLoading,
      refreshUserAndPricelist, 
      userGroups, 
      setUserGroups,
      currentTheme, 
      setCurrentTheme,
      providerTheme,
      setProviderTheme,
      companyLogo,
      setCompanyLogo,
      activeProvider,
      setActiveProvider,
      userClass,
      setUserClass,
      providerInfo,
      setProviderInfo,
      setUserTheme
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
